import React, { useState, useEffect } from 'react';
import { useLanguage } from '../i18n/LanguageContext.js';
import { useAuditLogger } from '../hooks/UseAuditLogger.js';

export default function ProfessionalSearch() {
  const { strings } = useLanguage();
  const { logEvent } = useAuditLogger();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [popularSearches, setPopularSearches] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const backendUrl = process.env.REACT_APP_PROFESSIONALS_SEARCH_URL || '';

  // Fetch popular searches once on component mount (onload)
  useEffect(() => {
    const controller = new AbortController();

    const fetchPopularSearches = async () => {
      try {
        const response = await fetch(`${backendUrl}/popular-searches`, {
          signal: controller.signal
        });
        if (response.ok) {
          const data = await response.json();
          const parsedResults = Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];
          setPopularSearches(parsedResults);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Cannot retrieve popular searches:', err);
        }
      }
    };

    fetchPopularSearches();

    return () => {
      controller.abort();
    };
  }, [backendUrl]);

  // Dynamic search on query change
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setShowResults(false);
      return;
    }

    const controller = new AbortController();

    const fetchSuggestions = async () => {
      try {
        const response = await fetch(
          `${backendUrl}/suggest?q=${encodeURIComponent(query)}`,
          { signal: controller.signal }
        );
        if (response.ok) {
          const data = await response.json();
          const parsedResults = Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : [];
          setResults(parsedResults);
          setShowResults(true);

          // Audit search execution
          logEvent({
            eventType: 'directory.search_executed',
            action: 'READ',
            requestData: {
              searchQuery: query,
              resultCount: parsedResults.length
            }
          });
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Search request failed:', err);
        }
      }
    };

    const timer = setTimeout(fetchSuggestions, 2000);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, backendUrl, logEvent]);

  // Fetch complete details when a search record is clicked
  const handleRecordClick = async (recordId) => {
    if (!recordId) return;

    // Audit record selection
    logEvent({
      eventType: 'directory.record_view',
      action: 'READ',
      targetEntity: 'professionals',
      targetId: recordId,
      requestData: { searchQuery: query }
    });

    setLoadingRecord(true);
    try {
      const url = backendUrl.includes('.php')
        ? `${backendUrl}?id=${recordId}`
        : `${backendUrl}/professionals/${recordId}`;

      const response = await fetch(url);
      if (response.ok) {
        const fullData = await response.json();
        setSelectedRecord(fullData);
      } else {
        console.error('Failed to load professional record');
      }
    } catch (err) {
      console.error('Error fetching professional details:', err);
    } finally {
      setLoadingRecord(false);
    }
  };

  const handleSearchSubmit = (searchQuery) => {
    const targetQuery = (searchQuery || query).trim();
    if (!targetQuery) return;

    setPopularSearches((prev) => {
      const filtered = prev.filter(
        (item) => item.toLowerCase() !== targetQuery.toLowerCase()
      );
      return [targetQuery, ...filtered].slice(0, 8);
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // Handlers for outbound contact events with audit logging
  const handleWebsiteClick = (record) => {
    logEvent({
      eventType: 'directory.website_visit_click',
      action: 'EXECUTE',
      targetEntity: 'professionals',
      targetId: record.id,
      requestData: { website: record.website }
    });

    window.open(record.website, '_blank', 'noopener,noreferrer');
  };

  const handleEmailClick = (record) => {
    logEvent({
      eventType: 'directory.contact_email_click',
      action: 'EXECUTE',
      targetEntity: 'professionals',
      targetId: record.id,
      requestData: { email: record.email }
    });

    window.location.href = `mailto:${record.email}?subject=Inquiry regarding ${encodeURIComponent(record.professionServices || 'services')}`;
  };

  // Maps active booleans from languagesSpoken to localized aria labels
  const formatLanguages = (languagesObj) => {
    if (!languagesObj) return '';

    const langMap = {
      russian: strings.lang?.ru_aria || 'Русский',
      english: strings.lang?.en_aria || 'English',
      german:  strings.lang?.de_aria || 'Deutsch',
      french:  strings.lang?.fr_aria || 'Français',
      other:   'Other'
    };

    if (Array.isArray(languagesObj)) {
      return languagesObj.join(', ');
    }

    if (typeof languagesObj === 'object') {
      return Object.entries(languagesObj)
        .filter(([_, isSpoken]) => Boolean(isSpoken))
        .map(([langKey]) => langMap[langKey] || langKey)
        .join(', ');
    }

    return '';
  };

  return (
    <div>
      {/* Search Bar */}
      <div className="search-bar-row">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={strings.pages.registration.searchBoxTitle}
          className="search-input"
        />
      </div>

      {/* Unified Search Results List */}
      {showResults && (
        <div className="search-results-dropdown">
          <div className="search-category-header">{strings.pages.registration.search_results}</div>
          <div className="search-category-body">
            {results.length > 0 ? (
              results.map((record, index) => (
                <div
                  key={record.id || index}
                  className="search-result-item single-line-record"
                  onClick={() => handleRecordClick(record.id)}
                  style={{ cursor: 'pointer', padding: '10px', borderBottom: '1px solid #eee' }}
                >
                  <strong>{record.fullName || `${record.firstName || ''} ${record.lastName || ''}`}</strong>
                  {record.company && <span> &bull; {record.company}</span>}
                  {(record.professionalService || record.professionServices) && (
                    <span> &bull; <em>{record.professionalService || record.professionServices}</em></span>
                  )}
                </div>
              ))
            ) : (
              <div className="search-no-results">
                {strings.pages.registration.companies_not_found}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading Modal State */}
      {loadingRecord && (
        <div className="modal-backdrop" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="modal-card" style={{ background: '#fff', padding: '24px', borderRadius: '8px' }}>
            <p>Loading professional details...</p>
          </div>
        </div>
      )}

      {/* Full Details Modal / Modal Card */}
      {selectedRecord && !loadingRecord && (
        <div className="modal-backdrop" onClick={() => setSelectedRecord(null)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{
            background: '#1B3047', padding: '24px', borderRadius: '8px', maxWidth: '500px', width: '90%'
          }}>
            
            <h3>{selectedRecord.firstName} {selectedRecord.lastName}</h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '110px 1fr',
              rowGap: '10px',
              fontSize: '12px',
              columnGap: '12px',
              marginTop: '16px',
              alignItems: 'baseline'
            }}>
              {selectedRecord.company && (
                <>
                  <span><strong>{strings.pages.registration.company}:</strong></span>
                  <span>{selectedRecord.company}</span>
                </>
              )}
              
              {selectedRecord.professionServices && (
                <>
                  <span><strong>{strings.pages.registration.service}:</strong></span>
                  <span>{selectedRecord.professionServices}</span>
                </>
              )}

              {/* Languages Spoken (using updated key: languagesSpoken) */}
              {(selectedRecord.languagesSpoken || selectedRecord.languages) && (
                <>
                  <span><strong>{strings.pages.registration.languages}:</strong></span>
                  <span>
                    {formatLanguages(selectedRecord.languagesSpoken || selectedRecord.languages)}
                  </span>
                </>
              )}
              
              {selectedRecord.placeOfOperation && (
                <>
                  <span><strong>{strings.pages.registration.location}:</strong></span>
                  <span>
                    {[selectedRecord.placeOfOperation.town, selectedRecord.placeOfOperation.canton].filter(Boolean).join(', ')}
                  </span>
                </>
              )}
              
              {selectedRecord.telephone && (
                <>
                  <span><strong>{strings.pages.registration.phone}:</strong></span>
                  <span>{selectedRecord.telephone}</span>
                </>
              )}
              
              {selectedRecord.employmentStatus && (
                <>
                  <span><strong>{strings.pages.registration.status}:</strong></span>
                  <span>{selectedRecord.employmentStatus}</span>
                </>
              )}
            </div>

            <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
              {selectedRecord.website && (
                <button
                  type="button"
                  className="popular-tag-btn"
                  onClick={() => handleWebsiteClick(selectedRecord)}
                >
                  {strings.pages.registration.visit_website}
                </button>
              )}

              {selectedRecord.email && (
                <button
                  type="button"
                  className="popular-tag-btn"
                  onClick={() => handleEmailClick(selectedRecord)}
                >
                  {strings.pages.registration.contact_via_email}
                </button>
              )}
              
              <button
                onClick={() => setSelectedRecord(null)}
                className="popular-tag-btn"
              >
                {strings.pages.registration.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popular Searches */}
      {popularSearches.length > 0 && (
        <div className="popular-searches-row">
          <span className="popular-label">
            {strings.pages.registration.searchPopular}:
          </span>
          <div className="popular-tags-list">
            {popularSearches.map((item, index) => (
              <button
                key={index}
                className="popular-tag-btn"
                onClick={() => {
                  setQuery(item);
                  handleSearchSubmit(item);
                }}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}